import { FeatureStyle } from "../featuretree/internal";
import { Obj, MEASUREMODE } from "../internal";
import { Cursor3d, DrawLineStringTool, DrawLRPointTool, DrawPointTool, DrawPolygonTool, EditFeatureTool, ElevationTool, FrameFeatureTool, GridTool, MeasureEdgeTool, OrthogonalMeasurementTool, PointCloudViewTool, ProfileTool, SelectFeatureTool, SnapShotTool, SurveyWheelMeasureTool, } from "./internal";
/**
 * tools handler
 * @class ToolHandler
 * @extends {Obj}
 * @constructor
 */
export class ToolHandler extends Obj {
    _addTooltoGroup(tool, group) {
        if (group) {
            if (this._toolgroups[group]) {
                this._toolgroups[group].push(tool);
            }
            else {
                this._toolgroups[group] = [tool];
            }
        }
    }
    constructor(scene) {
        super();
        this.tools = [];
        this._toolgroups = {};
        /**
         * FeatureCollections that the snap-function uses to snap on.
         * @type {Array}
         * @private
         */
        this.snaplayers = [];
        this.selectedfeatures = [];
        this.defaultstyles = {};
        this.scene = scene;
        this.engine = scene.engine;
        this._initDefaultStyles();
    }
    /**
     * Creates a Draw Polygon Tool.
     *
     * @method createDrawPolygonTool
     * @param {Object} options - The options for the drawing tool.
     * @param {string} name - The unique name of the drawing tool.
     * @param {string} [group] - The group affiliation of the drawing tool (optional).
     * @return {DrawPolygonTool} - The created Draw Polygon Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createDrawPolygonTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new DrawPolygonTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a Draw Point Tool.
     *
     * @method createDrawPointTool
     * @param {Object} options - The options for the drawing tool.
     * @param {string} name - The unique name of the drawing tool.
     * @param {string} [group] - The group affiliation of the drawing tool (optional).
     * @return {DrawPointTool} - The created Draw Point Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createDrawPointTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new DrawPointTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a Draw LineString Tool.
     *
     * @method createDrawLineStringTool
     * @param {Object} options - The options for the drawing tool.
     * @param {string} name - The unique name of the drawing tool.
     * @param {string} [group] - The group affiliation of the drawing tool (optional).
     * @return {DrawLineStringTool} - The created Draw LineString Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createDrawLineStringTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new DrawLineStringTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    createDrawLRPointTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new DrawLRPointTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    createPointCloudViewTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new PointCloudViewTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    createSnapShotTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new SnapShotTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a Profile Tool.
     *
     * @method createProfileTool
     * @param {Object} options - The options for the profile tool.
     * @param {string} name - The unique name of the profile tool.
     * @param {string} [group] - The group affiliation of the profile tool (optional).
     * @return {ProfileTool} - The created Profile Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createProfileTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new ProfileTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates an Elevation Tool.
     *
     * @method createElevationTool
     * @param {Object} options - The options for the elevation tool.
     * @param {string} name - The unique name of the elevation tool.
     * @param {string} [group] - The group affiliation of the elevation tool (optional).
     * @return {ElevationTool} - The created Elevation Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createElevationTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new ElevationTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates an Edit Feature Tool.
     *
     * @method createEditFeatureTool
     * @param {Object} options - The options for the edit feature tool.
     * @param {string} name - The unique name of the edit feature tool.
     * @param {string} [group] - The group affiliation of the edit feature tool (optional).
     * @return {EditFeatureTool} - The created Edit Feature Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createEditFeatureTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new EditFeatureTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a Selection Feature Tool.
     *
     * @method createSelectFeatureTool
     * @param {Object} options - The options for the selection feature tool.
     * @param {string} name - The unique name of the selection feature tool.
     * @param {string} [group] - The group affiliation of the selection feature tool (optional).
     * @return {SelectFeatureTool} - The created Selection Feature Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createSelectFeatureTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new SelectFeatureTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates an Orthogonal Measurement Tool.
     *
     * @method createOrthogonalMeasurementTool
     * @param {Object} options - The options for the orthogonal measurement tool.
     * @param {string} name - The unique name of the orthogonal measurement tool.
     * @param {string} [group] - The group affiliation of the orthogonal measurement tool (optional).
     * @return {OrthogonalMeasurementTool} - The created Orthogonal Measurement Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createOrthogonalMeasurementTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new OrthogonalMeasurementTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a Measure Edge Tool.
     *
     * @method createMeasureEdgeTool
     * @param {Object} options - The options for the measure edge tool.
     * @param {string} name - The unique name of the measure edge tool.
     * @param {string} [group] - The group affiliation of the measure edge tool (optional).
     * @return {MeasureEdgeTool} - The created Measure Edge Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createMeasureEdgeTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new MeasureEdgeTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a tool to draw a 3D Cursor in the scene.
     *
     * @method createCursor3d
     * @param {Object} options - The options for the 3D cursor tool.
     * @param {string} name - The unique name of the 3D cursor tool.
     * @param {string} [group] - The group affiliation of the 3D cursor tool (optional).
     * @return {Cursor3d} - The created 3D Cursor Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createCursor3d(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new Cursor3d(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Create a Grid-Tool
     * @method createGridTool
     * @param {Object} options
     * @return {GridTool}
     */
    createGridTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new GridTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Create tool to draw features per frame
     * @method createFrameFeatureTool
     * @param {Object} options
     * @returns {FrameFeatureTool}
     */
    createFrameFeatureTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new FrameFeatureTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    /**
     * Creates a tool to measure like a surveying wheel.
     *
     * @method createSurveyWheelMeasureTool
     * @param {Object} options - The options for the surveying wheel measure tool.
     * @param {string} name - The unique name of the surveying wheel measure tool.
     * @param {string} [group] - The group affiliation of the surveying wheel measure tool (optional).
     * @return {SurveyWheelMeasureTool} - The created Surveying Wheel Measure Tool.
     * @throws {Error} - Throws an error if no name is provided or if a tool with the same name already exists.
     */
    createSurveyWheelMeasureTool(options, name, group) {
        if (!name) {
            throw new Error("To create a tool you need to provide a unique name!");
        }
        if (this.getToolByName(name)) {
            throw new Error("A Tool with the same name exist already. Tool was not created!");
        }
        const tool = new SurveyWheelMeasureTool(this.scene, this, options, name, group);
        this.tools.push(tool);
        this._addTooltoGroup(tool, group);
        return tool;
    }
    disableAllTools() {
        for (let i = 0; i < this.tools.length; i++) {
            if (this.tools[i].enabled && this.tools[i]["options"]["singleactive"]) {
                this.tools[i].disable();
            }
        }
    }
    disableAllToolsOfType(type) {
        const toolsOfType = this.tools.filter(function (tool) {
            return tool.getType() == type;
        });
        for (let i = 0; i < toolsOfType.length; i++) {
            toolsOfType[i].disable();
        }
    }
    updateMeasureMode(measureMode) {
        //not used yet...
        switch (measureMode) {
            case MEASUREMODE.LEFTRIGHT:
                break;
            case MEASUREMODE.MONOPLOTTING:
                break;
            case MEASUREMODE.MULTIVIEW:
                break;
            default:
                break;
        }
    }
    addSnapLayer(snaplayer) {
        this.snaplayers.push(snaplayer);
    }
    removeSnapLayer(snaplayer) {
        for (let i = 0; i < this.snaplayers.length; i++) {
            if (snaplayer === this.snaplayers[i]) {
                this.snaplayers.splice(i, 1);
                return;
            }
        }
    }
    addSelectedFeature(feature) {
        this.selectedfeatures.push(feature);
    }
    removeSelectedFeature(feature) {
        for (let i = 0; i < this.selectedfeatures.length; i++) {
            if (feature === this.selectedfeatures[i]) {
                this.selectedfeatures.splice(i, 1);
            }
        }
    }
    clearSelectedFeatures() {
        this.selectedfeatures = [];
    }
    getLastSelectedFeature(types) {
        let selected = this.selectedfeatures;
        if (types && types.length > 0) {
            selected = this.selectedfeatures.filter(function (feature) {
                for (let i = 0; i < types.length; i++) {
                    if (feature.geometry) {
                        if (types[i] == feature.geometry.type)
                            return true;
                    }
                }
                return false;
            });
        }
        if (selected.length > 0)
            return selected[selected.length - 1];
        return null;
    }
    _updateSnapLayersOnTools() {
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            tool["options"]["snaplayer"] = this.snaplayers.length > 0 ? this.snaplayers : null;
        }
    }
    _initDefaultStyles() {
        const pointcolor = [1, 0.4, 0, 0.8];
        const linecolor = [1, 0.4, 0, 0.8];
        const fillcolor = [1, 0.4, 0, 0.15];
        const pointrule_cross = {
            filter: "",
            symbolizer: "point",
            params: {
                "font-marker": {
                    name: "cross",
                },
                size: 10,
                stroke: pointcolor,
            },
        };
        const pointrule_cross_large = {
            filter: "",
            symbolizer: "point",
            params: {
                "font-marker": {
                    name: "cross",
                },
                size: 20,
                stroke: pointcolor,
            },
        };
        const pointrule_dot = {
            filter: "",
            symbolizer: "point",
            params: {
                "font-marker": {
                    name: "circle",
                },
                size: 4,
                stroke: pointcolor,
            },
        };
        const pointrule_circle = {
            filter: "",
            symbolizer: "point",
            params: {
                "font-marker": {
                    name: "circle-o",
                },
                size: 10,
                stroke: pointcolor,
            },
        };
        const linerule_dashed = {
            filter: "",
            symbolizer: "line",
            params: {
                "stroke-width": 1.5,
                stroke: linecolor,
                "stroke-dash-width": 4,
                "stroke-gap-width": 2,
            },
        };
        const linerule_solid = {
            filter: "",
            symbolizer: "line",
            params: {
                "stroke-width": 2.1,
                stroke: linecolor,
            },
        };
        const ploygonrule = {
            filter: "",
            symbolizer: "polygon",
            params: {
                fill: fillcolor,
            },
        };
        this.defaultstyles["standard"] = new FeatureStyle({
            stylename: "measure_standard",
            rules: [pointrule_cross, linerule_dashed, ploygonrule],
        });
        this.defaultstyles["lookAt"] = new FeatureStyle({
            stylename: "lookAt_standard",
            rules: [pointrule_cross_large, linerule_dashed, ploygonrule],
        });
        this.defaultstyles["orthobase"] = new FeatureStyle({
            stylename: "measure_orthobase",
            rules: [pointrule_cross, linerule_solid, ploygonrule],
        });
        this.defaultstyles["profiledots"] = new FeatureStyle({
            stylename: "measure_profiledots",
            rules: [pointrule_dot, linerule_dashed, ploygonrule],
        });
        this.defaultstyles["draw"] = new FeatureStyle({
            stylename: "tool_draw",
            rules: [pointrule_circle, linerule_solid, ploygonrule],
        });
    }
    /**
     * Retrieves a tool by its name.
     * @param {string} name - The name of the tool to retrieve.
     * @return {Tool | undefined} Returns the tool with the specified name, or undefined if not found.
     */
    getToolByName(name) {
        const toolsOfName = this.tools.filter((tool) => tool.getName() == name);
        return toolsOfName[0];
    }
    /**
     * Retrieves tools belonging to a specified group.
     * @param {string} group - The name of the tool group to retrieve tools from.
     * @return {Tool[]} Returns an array of tools belonging to the specified group, or an empty array if the group is not found.
     */
    getToolsByGroup(group) {
        return this._toolgroups[group] || [];
    }
    removeTool(tool) {
        tool.destroy();
        // Remove tool from tools array
        this._removeItemFromArray(tool, this.tools);
        // Remove tool from toolgroups obj
        if (tool.getGroup()) {
            this._removeItemFromArray(tool, this._toolgroups[tool.getGroup()]);
        }
    }
    _removeItemFromArray(item, array) {
        const index = array.findIndex((i) => i === item);
        if (index !== -1) {
            array.splice(index, 1);
        }
    }
}
//# sourceMappingURL=toolhandler.js.map